/**
 * Cosmo: one COSMO pendant, measured from its CAD: six flattened pebbles at
 * six heights, grown together by six waisted necks into a loop with the high
 * centre pebble tied in twice, hung on three steel cables from a round canopy.
 * The pointer's x turns it on its cables, as a hand would; the slider is how
 * far it turns at the edge of the stage, in degrees.
 */
const {
  Cam, circ, clamp, fillet, fit, hull, poly, proj, rad, ringAt, seg,
  spring, stepS, disposer, mk, pointer, register,
} = HL;

const N = 26, SC = 0.135, PR = 228, PH = 265;
// pebbles from the CAD (mm): x, y across, foot height; the centre one rides highest
const PEB = { L: [-498, 8, 306], B: [-157, -498, 0], RT: [440, 299, 434], C: [4, 19, 597], RB: [445, -307, 208], TL: [-182, 527, 155] };
const NECK = [["TL", "L"], ["L", "B"], ["B", "RB"], ["RB", "RT"], ["C", "L"], ["C", "RT"]];
const CABLE = ["TL", "RT", "B"], CANOPY = 760, REST = 18;
const PROF = [[0, 0.42], [0.12, 0.8], [0.38, 1], [0.66, 0.97], [0.88, 0.8], [1, 0.62]];
const CAM = [0.665, 0.665, 0.34];

const turnXY = ([x, y], a) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];

/** One pebble at (x, y), foot at z: its silhouette, and the LED ring set into its top. */
function pebble(P, x, y, z) {
  let pts = [];
  for (const [t, f] of PROF) pts = pts.concat(ringAt(P, circ(PR * f, N).map((q) => ({ ...q, u: q.u + x, v: q.v + y })), z + PH * t));
  const led = circ(PR * 0.36, N).map((q) => ({ ...q, u: q.u + x, v: q.v + y }));
  return [poly(hull(pts)), poly(ringAt(P, led, z + PH))];
}

/** A neck grown between two pebbles: wide where it leaves each one, waisted in the middle. */
function neck(a, b, wa, wm) {
  let nx = -(b[1] - a[1]), ny = b[0] - a[0];
  const l = Math.hypot(nx, ny) || 1; nx /= l; ny /= l;
  const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], o = (p, w, s) => [p[0] + nx * w * s, p[1] + ny * w * s];
  return poly(fillet([o(a, wa, 1), o(m, wm, 1), o(b, wa, 1), o(b, wa, -1), o(m, wm, -1), o(a, wa, -1)], [0.5, 30, 0.5, 0.5, 30, 0.5], 6));
}

function mount({ stage, svg, read }, value) {
  const bag = disposer();
  let turn = value;

  const C = Cam(45, 0.34, SC); // a lower eye than the 2:1 view, so the loop opens up as it does in the room
  const ext = [[-760, -760, 0], [760, 760, 0], [760, -760, 0], [-760, 760, 0], [0, 0, PEB.C[2] + PH + CANOPY + 60]];
  fit(C, ext, 200, 166);
  const P = proj(C);

  const g = mk("g", {}, svg);
  // the canopy, a round aluminium disc on the ceiling, and three cables down to three pebbles
  const can = circ(110, N), TOP = PEB.C[2] + PH + CANOPY;
  const canopy = mk("path", { d: poly(hull(ringAt(P, can, TOP).concat(ringAt(P, can, TOP + 45)))), class: "sil" }, g);
  const cables = mk("path", { class: "nf lo" }, g);
  const body = mk("g", {}, g);
  const items = Object.keys(PEB).map((k, i) => { const grp = mk("g", {}, body); grp.dataset.k = "p" + i; return { k, grp, sil: mk("path", { class: "sil" }, grp), led: mk("path", { class: "nf lo" }, grp) }; });
  const necks = NECK.map(([a, b], i) => { const grp = mk("g", {}, body); grp.dataset.k = "n" + i; return { a, b, grp, sil: mk("path", { class: "sil" }, grp) }; });

  const sp = spring(REST);
  let last = null, order = "";
  function draw() {
    if (last !== null && Math.abs(last - sp.x) < 0.02) return;
    last = sp.x;
    const a = rad(sp.x), at = {};
    for (const [k, [x, y, z]] of Object.entries(PEB)) { const [u, v] = turnXY([x, y], a); at[k] = [u, v, z]; }
    const mid = (k) => P(at[k][0], at[k][1], at[k][2] + PH * 0.42);
    const dep = (q, z) => q[0] * CAM[0] + q[1] * CAM[1] + z * CAM[2];
    const depth = [];
    items.forEach((it) => {
      const [u, v, z] = at[it.k], [s, l] = pebble(P, u, v, z);
      it.sil.setAttribute("d", s); it.led.setAttribute("d", l);
      depth.push([dep(at[it.k], z + PH / 2), it.grp]);
    });
    necks.forEach((n) => {
      n.sil.setAttribute("d", neck(mid(n.a), mid(n.b), PR * 0.42 * SC, PR * 0.27 * SC));
      const A = at[n.a], Bq = at[n.b];
      // a neck sits just behind the nearer of the two pebbles it joins
      depth.push([Math.min(dep(A, A[2] + PH / 2), dep(Bq, Bq[2] + PH / 2)) - 1, n.grp]);
    });
    // back to front (rule 06): move the groups only when the order changes
    depth.sort((p, q) => p[0] - q[0]);
    const key = depth.map((d) => d[1].dataset.k).join("");
    if (key !== order) { order = key; depth.forEach(([, el]) => body.appendChild(el)); }
    cables.setAttribute("d", CABLE.map((k, i) => {
      const t = rad(i * 120 + 30) + a;
      return seg(P(Math.cos(t) * 80, Math.sin(t) * 80, TOP), P(at[k][0], at[k][1], at[k][2] + PH));
    }).join(""));
  }

  const B = register(stage, (dt) => { const m = stepS(sp, dt); draw(); return m; });
  bag.add(B.unregister);

  // the pointer's x turns the pendant; it reads the pointer only, never the drawing (rule 01)
  let on = null;
  const lit = (v) => { items.forEach((it) => it.sil.classList.toggle("hi", v)); necks.forEach((n) => n.sil.classList.toggle("hi", v)); canopy.classList.toggle("hi", !v); };
  function move([x]) {
    const f = clamp((x - 200) / 160, -1, 1);
    sp.t = REST + f * turn;
    if (on !== true) { on = true; lit(true); }
    read.textContent = `${f >= 0 ? "+" : "−"}${Math.abs(Math.round(f * turn))}°`;
    B.wake();
  }
  function leave() { sp.t = REST; on = false; lit(false); read.textContent = "rest"; B.wake(); }

  draw(); leave();
  bag.add(pointer(stage, { move, leave }));
  bag.add(() => svg.replaceChildren());
  return { set: (v) => { turn = v; }, destroy: bag.dispose };
}

hairline({
  name: "cosmo",
  means: "One COSMO from its CAD, six pebbles grown into a loop on three cables: the pointer turns it on its cables.",
  rules: [1, 5, 6, 8],
  range: [20, 45, 80],
  tour: [[90, 200], [320, 200], [200, 160], null],
  mount,
});
